/** Range Planning "Menus" list (Demo Mode only). Modelled on the RangePlanningItems list
 * shell (search + filter + table) - backed by the new menusClient mocked API, see
 * src/app/services/api/menus. */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { Pill } from '../../components/shared/Pill';
import CustomPagination from '../../components/forms/Pagination';
import { Plus, SearchLg } from '../../assets';
import useMenusList from './hooks/useMenusList';
import useMenusApi from './hooks/useMenusApi';
import { MenuStatus } from './types';

const QuickFilterDivider = () => (
  <div style={{ width: 1, height: 24, backgroundColor: '#E0E0E0', margin: '0 8px' }} />
);

const statusColor: Record<MenuStatus, 'success' | 'warning' | 'info' | 'default'> = {
  [MenuStatus.Live]: 'success',
  [MenuStatus.Scheduled]: 'warning',
  [MenuStatus.Draft]: 'info',
  [MenuStatus.Archived]: 'default',
};

const PAGE_SIZE = 20;

const MenusList = () => {
  const { t } = useTranslation('menus');
  const navigate = useNavigate();
  const { menus, totalCount, isLoading, loadMenus } = useMenusList();
  const { createMenu } = useMenusApi();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newMenuName, setNewMenuName] = useState('');
  const [newMenuDescription, setNewMenuDescription] = useState('');

  useEffect(() => {
    loadMenus(search, status, page, PAGE_SIZE);
  }, [loadMenus, search, status, page]);

  const maxPages = useMemo(() => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)), [totalCount]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(0);
  };

  const handleCreate = async () => {
    if (!newMenuName.trim()) return;
    await createMenu({ name: newMenuName, description: newMenuDescription });
    setShowCreateDialog(false);
    setNewMenuName('');
    setNewMenuDescription('');
    loadMenus(search, status, page, PAGE_SIZE);
  };

  return (
    <PageArea>
      <PageTitle>{t('title', 'Menus')}</PageTitle>

      <Typography variant="textSM"
        color="text.secondary"
        sx={{ mt: 2, mb: 4 }}>
        {t('intro', 'Menu authoring - service periods, structure, sites, costing, allergens & dietary, carbon and publishing in one place.')}
      </Typography>

      {/* Quick filters + search row */}
      <Grid container
        spacing={2}
        alignItems="center"
        sx={{ mb: 4 }}>
        <Grid item
          sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            size="small"
            label={t('search', 'Search')}
            placeholder={t('searchPlaceholder', 'Search by menu name')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 260 }}
          />
        </Grid>
        <Grid item>
          <Button
            onClick={handleSearch}
            size="lg"
            startIcon={<SvgIcon><SearchLg /></SvgIcon>}
          >
            {t('search', 'Search')}
          </Button>
        </Grid>
        <Grid item>
          <QuickFilterDivider />
        </Grid>
        <Grid item>
          <Grid container
            spacing={1}
            alignItems="center">
            <Grid item>
              <Chip
                label={t('allStatuses', 'All statuses')}
                color={status === '' ? 'primary' : 'default'}
                onClick={() => { setStatus(''); setPage(0); }}
              />
            </Grid>
            {Object.values(MenuStatus).map((s) => (
              <Grid item
                key={s}>
                <Chip
                  label={t(`status.${s}`, s)}
                  color={status === s ? 'primary' : 'default'}
                  onClick={() => { setStatus((prev) => (prev === s ? '' : s)); setPage(0); }}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      <Section>
        <TableHeader>
          {t('title', 'Menus')}
          <Pill text={`${totalCount} ${t('title', 'Menus')}`} />
          <Box sx={{ ml: 'auto' }}>
            <Button
              onClick={() => setShowCreateDialog(true)}
              startIcon={<SvgIcon><Plus /></SvgIcon>}
            >
              {t('createMenu', 'Create Menu')}
            </Button>
          </Box>
        </TableHeader>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('name', 'Name')}</TableCell>
                <TableCell>{t('menuTypeLabel', 'Menu Type')}</TableCell>
                <TableCell>{t('servicePeriods', 'Service Periods')}</TableCell>
                <TableCell>{t('sites', 'Sites')}</TableCell>
                <TableCell align="right">{t('targetMargin', 'Target Margin')}</TableCell>
                <TableCell>{t('statusLabel', 'Status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && menus.length === 0 && (
                <TableRow><TableCell colSpan={6}>{t('noMenus', 'No menus match this filter.')}</TableCell></TableRow>
              )}
              {menus.map((menu) => (
                <TableRow
                  key={menu.id}
                  hover
                  onClick={() => navigate(`/menus/${menu.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{menu.name}</TableCell>
                  <TableCell>{t(`menuType.${menu.menuType}`, menu.menuType)}</TableCell>
                  <TableCell>{menu.servicePeriods.map((p) => p.name).join(', ')}</TableCell>
                  <TableCell>{menu.sites.length}</TableCell>
                  <TableCell align="right">{menu.targetMarginPercent}%</TableCell>
                  <TableCell>
                    <Chip label={t(`status.${menu.status}`, menu.status)}
                      color={statusColor[menu.status]}
                      size="small" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!isLoading && (
          <CustomPagination
            page={page}
            setPage={setPage}
            maxPages={maxPages}
            breakpointForChangeDisplay={120000}
            useNoDataMessage={false}
          />
        )}
      </Section>

      <Dialog open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('createMenu', 'Create Menu')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mt: 2 }}>
            <TextField
              label={t('name', 'Name')}
              value={newMenuName}
              onChange={(e) => setNewMenuName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label={t('description', 'Description')}
              value={newMenuDescription}
              onChange={(e) => setNewMenuDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={() => setShowCreateDialog(false)}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button onClick={handleCreate}
            disabled={!newMenuName.trim()}>
            {t('create', 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default MenusList;
